import { CommonModule } from '@angular/common';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { catchError, forkJoin, map, Observable, of, retry, switchMap, take, tap, finalize } from 'rxjs';

import { SvcDialogMode, SvcDialogService } from '@solvace/component/svc-dialogs';
import { HttpErrorService } from 'projects/lib-shared-common/src/lib/services/http-error.service';
import { UserService } from 'projects/lib-shared-core/src/public-api';
import { LibSharedCommonModule } from 'projects/lib-shared-common/src/public-api';

import {
  ActionPlanChecklistCompletionInfo,
  ActionPlanChecklistRequest,
  ActionPlanChecklistResponse,
  SvcModalActionPlanByIdResponse,
  UpdateActionPlanChecklistItemRequest,
} from '../../interfaces/svc-modal-action-plan.interface';
import { SvcActionPlanChecklistService } from '../../services/svc-action-plan-checklist.service';
import { SvcActionPlanPermissionService } from '../../services/svc-action-plan-permission.service';

@Component({
  selector: 'svc-action-plan-checklist-tab',
  standalone: true,
  imports: [
    CommonModule,
    LibSharedCommonModule,
    ReactiveFormsModule,
    TranslocoModule,
    DragDropModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatCheckboxModule,
  ],
  templateUrl: './svc-action-plan-checklist-tab.component.html',
  styleUrls: ['./svc-action-plan-checklist-tab.component.scss'],
})
export class SvcActionPlanChecklistTabComponent {
  public readonly actionPlanId = input<number | null>(null);
  // Action plan payload — the source of truth for permission rules. Optional
  // so the tab still renders during create mode (no plan yet).
  public readonly actionPlan = input<SvcModalActionPlanByIdResponse | null>(null);
  // Parent-provided checklist payload. The parent fetches it once when the
  // modal opens; this tab only mounts when the user clicks the Checklist tab,
  // so consuming the parent state avoids a duplicate GET.
  public readonly initialChecklist = input<ActionPlanChecklistResponse | null>(null);
  public readonly mode = input<SvcDialogMode>(SvcDialogMode.add);

  public readonly changesPersisted = output<void>();

  readonly #destroyRef = inject(DestroyRef);
  readonly #fb = inject(FormBuilder);
  readonly #errorService = inject(HttpErrorService);
  readonly #userService = inject(UserService);
  readonly #checklistService = inject(SvcActionPlanChecklistService);
  readonly #permissions = inject(SvcActionPlanPermissionService);
  readonly #dialogService = inject(SvcDialogService);
  readonly #translocoService = inject(TranslocoService);

  public readonly checklistInfo = signal<ActionPlanChecklistCompletionInfo | null>(null);
  public readonly checklistHasFilledItems = signal<boolean>(false);
  public readonly checklistSaveInProgress = signal<boolean>(false);

  // Snapshot of taskUniqueIds loaded from the backend on the most recent
  // `#applyChecklistResponse`. The trash button no longer fires DELETE
  // immediately — deletions are queued and persisted by `saveChecklist` —
  // so we need this baseline to compute which persisted items have been
  // removed by the user.
  #initialTaskUniqueIds = new Set<number>();

  // Last `initialChecklist` payload the constructor effect applied. The effect
  // also tracks `mode()`, so without this identity guard every view↔edit
  // transition re-applied the snapshot captured at modal open, wiping items
  // saved/toggled after that (BUG-69196: rows vanish on entering edit mode and
  // only reappear after the next save reloads from the backend).
  #lastAppliedInitialChecklist: ActionPlanChecklistResponse | null = null;

  // Set when a name changes on a saved plan; the blur handler persists inline.
  #pendingNameCommit = false;

  public get isPlanConcluded(): boolean {
    return this.#permissions.isPlanConcluded(this.actionPlan());
  }

  public get canModify(): boolean {
    return this.#permissions.canModifyChecklist(this.actionPlan());
  }

  public get canToggleStatus(): boolean {
    return this.#permissions.canToggleChecklistItem(this.actionPlan());
  }

  public readonly checklistForm: FormGroup = this.#fb.group({
    checklistItems: this.#fb.array([]),
  });

  constructor() {
    effect(() => {
      const id = this.actionPlanId();
      const initial = this.initialChecklist();
      const currentMode = this.mode();

      if (!id) {
        // No id yet — open with an empty list in every mode. The user is the
        // one driving when the first row appears: clicking the "+" button is
        // the explicit signal. Pre-seeding a blank row in add mode hid that
        // affordance and made the empty state look already in-progress.
        this.checklistItemsFormArray.clear({ emitEvent: false });
        this.#lastAppliedInitialChecklist = null;
        // `currentMode` is read so this effect re-runs on mode transitions
        // without leaving stale rows behind.
        void currentMode;
        this.#updateInfo();
        return;
      }

      if (initial && initial !== this.#lastAppliedInitialChecklist) {
        // Apply each parent payload exactly once. Re-running on `mode()`
        // changes must NOT rebuild the form array from a stale snapshot —
        // the current rows already reflect newer state (checklist-only saves
        // and checkbox toggles done since the modal opened).
        this.#lastAppliedInitialChecklist = initial;
        this.#applyChecklistResponse(initial);
      }
      // If id is set but parent hasn't supplied the response yet, wait for
      // the next signal change — the effect re-runs when initialChecklist
      // arrives.
    });

    effect(() => {
      // Track signals consumed by canModify so the form enable/disable stays
      // in sync as the action plan / mode changes.
      this.actionPlanId();
      this.actionPlan();
      this.#applyModeRules();
    });
  }

  public get checklistItemsFormArray(): FormArray<FormGroup> {
    return this.checklistForm.controls['checklistItems'] as FormArray<FormGroup>;
  }

  public get checklistItemsControls(): FormGroup[] {
    return (this.checklistItemsFormArray?.controls as FormGroup[]) ?? [];
  }

  public get hasChecklistItemsToSave(): boolean {
    return this.checklistHasFilledItems();
  }

  public get hasChecklistChanges(): boolean {
    return !!this.checklistItemsFormArray?.dirty;
  }

  // True when at least one persisted item from the last loaded snapshot is no
  // longer present in the form. Used by the parent's Save-button gate so the
  // button stays visible after a user removes every existing item.
  public get hasPendingDeletions(): boolean {
    return this.#getPendingDeletionIds().length > 0;
  }

  /**
   * True when any rendered item has an empty (or whitespace-only) task name.
   * The modal consults this before firing a save so the user is prompted to
   * fill the empty row instead of having `saveChecklist` filter it out
   * silently — matching the existing "Preencha o campo selecionado" alert
   * that already guards `addChecklistItem`.
   */
  public hasEmptyChecklistInput(): boolean {
    return this.checklistItemsControls.some(
      (group) => !String(group.get('taskName')?.value ?? '').trim()
    );
  }

  /** Opens the same "Preencha o campo selecionado" alert as addChecklistItem. */
  public showFillFieldAlert(): void {
    this.#showFillFieldAlert();
  }

  #getPendingDeletionIds(): number[] {
    if (!this.#initialTaskUniqueIds.size) return [];
    const remaining = new Set(
      this.checklistItemsControls
        .map((group) => group.get('taskUniqueId')?.value as number | null)
        .filter((id): id is number => id != null)
    );
    return Array.from(this.#initialTaskUniqueIds).filter((id) => !remaining.has(id));
  }

  /**
   * Refresh the checklist from the backend. Used after writes (saveChecklist,
   * removeChecklistItem) to repopulate the form with persisted data. The
   * initial population on tab mount comes from the `initialChecklist` input.
   */
  public loadChecklist(idOverride?: number | null): void {
    const id = idOverride ?? this.actionPlanId();
    if (!id) {
      this.#updateInfo();
      return;
    }

    this.#checklistService
      .getChecklist(id)
      .pipe(
        tap((checklist) => this.#applyChecklistResponse(checklist)),
        catchError((err) => {
          this.#errorService.showErrorInToast(err);
          return of(null);
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  #applyChecklistResponse(checklist: ActionPlanChecklistResponse | null | undefined): void {
    this.checklistItemsFormArray.clear({ emitEvent: false });
    this.#initialTaskUniqueIds.clear();
    (checklist?.items ?? [])
      .sort((a, b) => a.taskOrder - b.taskOrder)
      .forEach((item) => {
        this.checklistItemsFormArray.push(
          this.#buildItem(item.taskName, item.isCompleted, item.taskOrder, item.taskUniqueId)
        );
        if (item.taskUniqueId != null) this.#initialTaskUniqueIds.add(item.taskUniqueId);
      });
    this.checklistItemsFormArray.markAsPristine();
    this.checklistItemsFormArray.markAsUntouched();
    this.#updateInfo();
  }

  public addChecklistItem(): void {
    if (!this.canModify) return;

    const hasEmptyItem = this.checklistItemsControls.some(
      (group) => !String(group.get('taskName')?.value ?? '').trim()
    );
    if (hasEmptyItem) {
      this.#showFillFieldAlert();
      return;
    }

    const order = this.checklistItemsControls.length;
    this.checklistItemsFormArray.push(this.#buildItem('', false, order));
    this.#updateInfo();
    // On a saved plan the new row is persisted inline once its name is committed
    // on blur, so it must not dirty the array (a dirty array flips the modal
    // footer to the checklist "Save" button). Only a not-yet-created plan keeps
    // the queue-and-save flow.
    if (!this.actionPlanId()) {
      this.checklistItemsFormArray.markAsDirty();
    }
  }

  public removeChecklistItem(index: number): void {
    if (!this.canModify) return;
    if (!this.checklistItemsControls?.length) return;

    // Deletion is deferred — the row is removed locally and the persisted
    // taskUniqueId (if any) is left in `#initialTaskUniqueIds` so that
    // `saveChecklist` can issue the DELETE alongside any pending creates and
    // updates. Confirming everything under a single Save matches the add/edit
    // UX and keeps the "Save" button visible while there is unpersisted intent.
    this.checklistItemsFormArray.removeAt(index);
    this.#updateInfo();
    const actionPlanId = this.actionPlanId();
    if (actionPlanId) {
      this.#persistChecklist(actionPlanId);
    } else {
      this.checklistItemsFormArray.markAsDirty();
    }
  }

  public dropChecklist(event: CdkDragDrop<FormGroup[]>): void {
    if (!this.canModify) return;
    if (event.previousIndex === event.currentIndex) return;
    // Keep the drop handler minimal — same pattern as quiz-questions-tab —
    // so the CDK transitions are not interrupted by Angular re-renders. The
    // taskOrder is recomputed from the array position at save time
    // (`saveChecklist`), so there is no need to push it into each FormGroup
    // here. `#updateInfo()` refreshes `checklistHasFilledItems` so the parent's
    // Save-button gate reacts to reorder events as well as add/edit.
    moveItemInArray(this.checklistItemsFormArray.controls, event.previousIndex, event.currentIndex);
    this.#updateInfo();
    const actionPlanId = this.actionPlanId();
    if (actionPlanId) {
      this.#persistChecklist(actionPlanId);
    } else {
      this.checklistItemsFormArray.markAsDirty();
    }
  }

  public toggleChecklistItemStatus(index: number): void {
    if (!this.canToggleStatus) return;
    // `formControlName` has already written the new value to the FormGroup by
    // the time `(change)` fires, so we recompute the counter optimistically —
    // waiting for the PUT round-trip made the tab badge lag a full network
    // RTT behind the checkbox state.
    this.#updateInfo();

    const group = this.checklistItemsFormArray.at(index) as FormGroup;
    const taskUniqueId = group.get('taskUniqueId')?.value;
    const isCompleted = group.get('isCompleted')?.value;
    const actionPlanId = this.actionPlanId();

    if (taskUniqueId && actionPlanId) {
      this.checklistItemsFormArray.markAsPristine();
      this.#checklistService
        .updateChecklistItemStatus(actionPlanId, taskUniqueId, isCompleted, this.#resolveSiteId())
        .pipe(
          retry({ count: 2, delay: 300 }),
          takeUntilDestroyed(this.#destroyRef)
        )
        .subscribe(() => this.changesPersisted.emit());
    } else {
      this.checklistItemsFormArray.markAsDirty();
    }
  }

  public onChecklistItemChanged(): void {
    this.#updateInfo();
    if (this.actionPlanId()) {
      this.#pendingNameCommit = true;
    } else {
      this.checklistItemsFormArray.markAsDirty();
    }
  }

  public commitChecklistItem(): boolean {
    const actionPlanId = this.actionPlanId();
    if (!actionPlanId || !this.#pendingNameCommit) return false;
    this.#pendingNameCommit = false;
    this.#persistChecklist(actionPlanId);
    return true;
  }

  #persistChecklist(actionPlanId: number): void {
    this.saveChecklist(actionPlanId, false)
      .pipe(take(1))
      .subscribe((ok) => {
        if (ok) this.changesPersisted.emit();
      });
  }

  #syncInitialTaskUniqueIds(): void {
    this.#initialTaskUniqueIds.clear();
    this.checklistItemsControls.forEach((group) => {
      const id = group.get('taskUniqueId')?.value as number | null;
      if (id != null) this.#initialTaskUniqueIds.add(id);
    });
  }

  public trackByIndex(index: number): number {
    return index;
  }

  public saveChecklist(actionPlanId: number, reload = true): Observable<boolean> {
    const itemsToSave = this.checklistItemsControls
      .map((group, index) => ({
        group,
        taskUniqueId: group.get('taskUniqueId')?.value as number | null,
        taskName: String(group.get('taskName')?.value ?? '').trim(),
        isCompleted: !!group.get('isCompleted')?.value,
        taskOrder: index,
      }))
      .filter((item) => !!item.taskName)
      .map((item, index) => ({ ...item, taskOrder: index }));

    this.checklistHasFilledItems.set(itemsToSave.length > 0);

    const deletionIds = this.#getPendingDeletionIds();
    if (!itemsToSave.length && !deletionIds.length) return of(true);

    const siteId = this.#resolveSiteId();
    const currentUserId = this.#userService.userId$;

    const requests: Observable<void | number>[] = [
      ...deletionIds.map((id) => this.#checklistService.deleteChecklistItem(actionPlanId, id)),
      ...itemsToSave.map((item) => {
        if (item.taskUniqueId) {
          const updateRequest: UpdateActionPlanChecklistItemRequest = {
            taskUniqueId: item.taskUniqueId,
            actionPlanUniqueId: actionPlanId,
            taskName: item.taskName,
            taskOrder: item.taskOrder,
            lastUpdateUserId: currentUserId,
            siteId,
          };
          return this.#checklistService.updateChecklistItem(actionPlanId, updateRequest);
        }
        // The POST /Checklist endpoint silently ignores isCompleted in the body
        // (verified via Swagger: 201 Created but the persisted row has
        // isCompleted=false). So when the user saves a new item already marked
        // completed, we must follow the create with a PATCH /Complete. retry()
        // covers the brief backend lag where the freshly-created row is not yet
        // visible to the PATCH (QA-reported intermittent symptom: item saved as
        // completed but reopens as not completed).
        const createRequest: ActionPlanChecklistRequest = {
          actionPlanUniqueId: actionPlanId,
          taskName: item.taskName,
          taskOrder: item.taskOrder,
          createdUserId: currentUserId,
          siteId,
        };
        return this.#checklistService.createChecklistItem(actionPlanId, createRequest).pipe(
          switchMap((newTaskUniqueId) => {
            if (newTaskUniqueId) {
              item.group.get('taskUniqueId')?.setValue(newTaskUniqueId, { emitEvent: false });
            }
            if (!item.isCompleted || !newTaskUniqueId) return of(undefined);
            return this.#checklistService
              .updateChecklistItemStatus(actionPlanId, newTaskUniqueId, true, siteId)
              .pipe(retry({ count: 2, delay: 300 }));
          })
        );
      }),
    ];

    if (!requests.length) return of(true);

    this.checklistSaveInProgress.set(true);
    return forkJoin(requests).pipe(
      tap(() => {
        this.checklistItemsFormArray.markAsPristine();
        this.checklistItemsFormArray.markAsUntouched();
        if (reload) {
          this.loadChecklist();
        } else {
          this.#syncInitialTaskUniqueIds();
          this.#updateInfo();
        }
      }),
      map(() => true),
      catchError((err) => {
        this.#errorService.showErrorInToast(err);
        this.loadChecklist();
        return of(false);
      }),
      finalize(() => this.checklistSaveInProgress.set(false))
    );
  }

  /**
   * Site scope sent on checklist mutations. Checklist items belong to the
   * action plan's site, which can differ from the logged-in user's current
   * site in multi-site environments — sending the user's site made the
   * backend reject the writes with 400 "You are not allowed to modify the
   * checklist or the action plan is complete" (BUG-69194, Sandbox EFESO).
   * Falls back to the user's site while the plan is being created (no
   * payload yet), when both sites are the same by definition.
   */
  #resolveSiteId(): number {
    const planSiteId = Number(this.actionPlan()?.siteId ?? 0);
    if (Number.isFinite(planSiteId) && planSiteId > 0) return planSiteId;
    return Number(this.#checklistService.getCurrentSiteId() ?? 0);
  }

  #buildItem(taskName = '', isCompleted = false, taskOrder = 0, taskUniqueId?: number): FormGroup {
    return this.#fb.group({
      taskUniqueId: [taskUniqueId ?? null],
      taskName: [taskName, [Validators.required, Validators.maxLength(50)]],
      isCompleted: [isCompleted],
      taskOrder: [taskOrder],
    });
  }

  #showFillFieldAlert(): void {
    this.#dialogService
      .openConfirm(this.#translocoService.translate('Preencha o campo selecionado'), {
        title: this.#translocoService.translate('Atenção'),
        cancelText: '',
      })
      .pipe(take(1), takeUntilDestroyed(this.#destroyRef))
      .subscribe();
  }

  #applyModeRules(): void {
    if (this.canModify) {
      this.checklistForm.enable({ emitEvent: false });
    } else {
      this.checklistForm.disable({ emitEvent: false });
    }
  }

  #updateInfo(): void {
    const filledItems = this.checklistItemsControls.filter(
      (g) => !!String(g.get('taskName')?.value ?? '').trim()
    );
    const total = this.checklistItemsControls.length;
    const completed = this.checklistItemsControls.filter((g) => g.get('isCompleted')?.value).length;
    this.checklistHasFilledItems.set(filledItems.length > 0);
    this.checklistInfo.set({ total, completed });
  }
}
